import {
    Application,
    Comment,
    Hook,
    Model,
    Platform,
    TinyMCE,
    Toast,
    UserCurrent,
    Widget,
} from 'jamespot-front-business';
import { DriveOrFilebank } from 'jamespot-react-components';
import jApi, { AddCommentParams, jCommentList, UpdateCommentParams, WidgetWrapperProps } from 'jamespot-user-api';
import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import styled, { useTheme } from 'styled-components';
import useCommentRT from '../hooks/useCommentRT';
import useCommentsLikeRT from '../hooks/useCommentsLikeRT';
import { EditorsPanel } from './widgets/components/WidgetEditorPanel';
import { WidgetList } from './widgets/WidgetList/WidgetList';

import { useMarkdownModal } from '../hooks/useMarkdownModal';
import { useAppDispatch, useAppSelector } from '../redux/store';
import { commonComponentsRegistry } from '../registry/CommonRegistry';
import { getTinyMCEMentionsQueries } from '../utils/tinymce';
import { AddReportModal, AddReportModalProps } from './comments/AddReportModal';
import { CommentsBlocAddCommentWidgetsWrapper } from './comments/CommentsBlocAddCommentWidgetsWrapper';
import { CommentsBlocCommentWidgetsWrapper } from './comments/CommentsBlocCommentWidgetsWrapper';
import { MarkdownModal } from './tinymce/extension/MarkdownModal';

const JRCCommentsBloc = commonComponentsRegistry.getLazyComponent('JRCCommentsBloc');

export type CommentsBlocProps = {
    articleId: number;
    articleUri: string;
    variant?: 'modal' | 'inline';
    canComment: boolean;
    commentType?: 'comment' | 'commentResponse';
    socialQuestion?: {
        isResolved: boolean;
        responseId?: number;
        canSelectResponse: boolean;
    };
};

const CommentsBlocWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
    width: 100%;
    margin-top: 10px;
`;

export const CommentsBloc = ({
    articleId,
    variant = 'inline',
    canComment,
    commentType = 'comment',
    socialQuestion,
    articleUri,
}: CommentsBlocProps) => {
    const intl = useIntl();
    const theme = useTheme();
    const dispatch = useAppDispatch();

    const [token, setToken] = useState<string>();
    const [loading, setLoading] = useState(false);
    const [widgets, setWidgets] = useState<WidgetWrapperProps[]>([]);

    const [tokenLoading, setTokenLoading] = useState(false);

    const currentUser = useAppSelector(UserCurrent.selectors.get);
    const widgetIds = useAppSelector(Widget.selectors.selectWidgets);
    const tinyMCECommonOptions = useAppSelector(TinyMCE.selectors.selectTinyMCECommonOptions);
    const tinyMCEExtendedOptions = useAppSelector(TinyMCE.selectors.selectTinyMCEExtendedOptions);
    const models = useAppSelector(Model.selectors.selectAll);
    const platformConfig = useAppSelector(Platform.selectors.selectPlatformConfig);
    const onlyOffice = useAppSelector((state) => Application.selectors.selectById(state, 'OnlyOfficeHook'));
    const commentsList = useAppSelector((state) => Comment.selectors.commentList(state, articleId));
    const driveHook = useAppSelector((state) => Hook.selectors.selectHook(state, 'DriveHook'));
    const fileBank = useAppSelector((state) => Application.selectors.selectById(state, 'FileBankHook'));
    const widgetHook = useAppSelector((state) => Hook.selectors.selectHook(state, 'WidgetHook'));
    const [socialQuestionOptions, setSocialQuestionOptions] = useState(socialQuestion);
    const [reportProps, setReportProps] = useState<Omit<AddReportModalProps, 'onClose'> | null>(null);
    const {
        open,
        onInsert,
        close: closeMarkdownModal,
        ext: extMarkdown,
        isActive: isMarkdownActive,
    } = useMarkdownModal();

    useCommentRT(articleId);

    useCommentsLikeRT(
        commentsList.map((c) => c.id),
        articleId,
    );

    const onError = useCallback(
        (message?: string) => {
            dispatch(Toast.actions.error({ label: message ?? intl.formatMessage({ id: 'GLOBAL_Error' }) }));
        },
        [dispatch, intl],
    );

    const onCommentUpdate = async (args: UpdateCommentParams) => {
        const commentToUpdate: jCommentList | undefined = commentsList.find((item) => item.id === args.idComment);
        if (!commentToUpdate) return;
        return jApi.article
            .updateComment({
                idComment: args.idComment,
                description: args.description ?? commentToUpdate.description ?? undefined,
                token: args.token,
                widgets: args.widgets,
            })
            .then(() => {
                dispatch(Comment.actions.fetchComments({ idArticle: articleId }));
                fetchToken();
            })
            .catch((e) => {
                onError(e.errorMsg);
            });
    };

    const onCommentDelete = async (idComment: number) => {
        dispatch(Comment.actions.deleteComment({ idComment, idArticle: articleId }));
    };

    const onWidgetUpdate = () => {
        dispatch(Comment.actions.fetchComments({ idArticle: articleId }));
        fetchToken();
    };

    const fetchToken = useCallback(() => {
        if (!token && !tokenLoading) {
            setTokenLoading(true);
            jApi.network
                .token()
                .then((res) => {
                    setToken(res.result);
                    setTokenLoading(false);

                    // set comment token to our widget slice to be able to upload image inside our widgets
                    dispatch(Widget.slice.actions.setToken({ token: res.result }));
                })
                .catch((e) => {
                    onError(e.errorMsg);
                });
        }
    }, [onError, token, tokenLoading, dispatch]);

    const onComment = useCallback(
        async (params: Pick<AddCommentParams, 'content' | 'alertAuthor' | 'sendAlert'>) => {
            if (token) {
                setLoading(true);
                const finalWidget: WidgetWrapperProps[] = Object.keys(widgetIds)
                    .map((id) => {
                        if (widgets?.some((w) => w.widget.uniqid === id)) {
                            return widgetIds[id];
                        }
                        return null;
                    })
                    .filter((w): w is WidgetWrapperProps => w !== null);

                try {
                    // TODO : passer par le store pour avoir une réactivité directe après ajout (confirmée ensuite par retour RT)
                    //        -> état intermédiaire PENDING à gérer.
                    await jApi.article
                        .addComment({
                            ...params,
                            widgets: finalWidget,
                            token,
                            idArticle: articleId,
                            type: commentType,
                        })
                        .then(() => {
                            setWidgets([]);
                        });
                    fetchToken();
                } catch (e: any) {
                    onError(e.errorMsg);
                    throw e;
                } finally {
                    setLoading(false);
                }
            }
        },
        [articleId, commentType, fetchToken, onError, token, widgetIds, widgets],
    );

    useEffect(() => {
        fetchToken();
    }, [fetchToken]);

    useEffect(() => {
        dispatch(Comment.actions.fetchComments({ idArticle: articleId }));
    }, [articleId, dispatch]);

    useEffect(() => {
        if (!commentsList) {
            return;
        }
        commentsList.forEach((comment) => {
            comment._widgets?.forEach((widget) => {
                dispatch(Widget.slice.actions.registerWidgetObject({ uniqid: widget.widget.uniqid, object: comment }));
                dispatch(
                    Widget.slice.actions.registerWidgetObjectRights({
                        uniqid: widget.widget.uniqid,
                        rights: comment._right,
                    }),
                );
            });
        });
    }, [commentsList, dispatch]);

    return (
        <>
            {tinyMCECommonOptions && tinyMCEExtendedOptions && (
                <>
                    <CommentsBlocWrapper>
                        <JRCCommentsBloc
                            canComment={canComment}
                            loading={loading}
                            token={token}
                            comments={commentsList}
                            onComment={onComment}
                            onCommentUpdate={onCommentUpdate}
                            onCommentDelete={onCommentDelete}
                            isWidgetActive={!!(widgetHook?.isActive && widgetHook?.autorize?.includes('comment'))}
                            canCreateWidget={
                                !!(
                                    widgetHook?.isActive &&
                                    widgetHook?.create &&
                                    widgetHook?.autorize?.includes('comment')
                                )
                            }
                            widgetListComponent={
                                <WidgetList
                                    mode={'comment'}
                                    namespace={'comment'}
                                    onChange={(widget) => {
                                        setWidgets([widget]); // COMMENT DO NOT SUPPORT MULTI WIDGET
                                        dispatch(
                                            Widget.slice.actions.registerWidget({
                                                uniqid: widget.widget.uniqid,
                                                widget: widget,
                                            }),
                                        );
                                        setToken(undefined);
                                        fetchToken();
                                    }}
                                    view={'edit'}
                                />
                            }
                            newWidgetsWrapperComponent={<CommentsBlocAddCommentWidgetsWrapper widgets={widgets} />}
                            existingWidgetsWrapperComponent={(comment, widgets, mode, inplace = true) => (
                                <CommentsBlocCommentWidgetsWrapper
                                    comment={comment}
                                    widgets={widgets}
                                    mode={mode}
                                    inplace={inplace}
                                    handleWidgetUpdate={onWidgetUpdate}
                                    handleWidgetRemove={fetchToken}
                                />
                            )}
                            currentUser={currentUser}
                            tinyMCEConfig={{
                                commonOptions: {
                                    ...tinyMCECommonOptions,
                                    toolbar: tinyMCEExtendedOptions.commentsToolbar,
                                },
                                mentionsQueries: getTinyMCEMentionsQueries(models, theme, onError),
                            }}
                            onError={onError}
                            highlightFields={platformConfig.userHighlightFields}
                            userAccountStatus={platformConfig.userAccountStatus}
                            userModel={models.find((m) => m.type === 'user')}
                            isWedocActive={!!onlyOffice}
                            onGetHashError={(message?: string) =>
                                dispatch(
                                    Toast.actions.error({
                                        label: message ?? intl.formatMessage({ id: 'GLOBAL_Error' }),
                                    }),
                                )
                            }
                            activeDrives={
                                [
                                    ...(fileBank && driveHook ? ['fileBank'] : []),
                                    ...Hook.utils.buildDrivesArrayFromObject(driveHook?.drives),
                                ] as DriveOrFilebank[]
                            }
                            variant={variant}
                            handlers={{
                                drive: jApi.drive,
                                filebank: jApi.filebank,
                                file: jApi.file,
                                user: jApi.user,
                                socialAction: jApi.socialAction,
                                questionAnswer: jApi.questionAnswer,
                            }}
                            socialQuestion={
                                socialQuestionOptions
                                    ? {
                                          ...socialQuestionOptions,
                                          onSelectResponse: (responseId: number) => {
                                              setSocialQuestionOptions({
                                                  ...socialQuestionOptions,
                                                  responseId,
                                                  isResolved: true,
                                              });
                                          },
                                          onUnselectResponse: () => {
                                              setSocialQuestionOptions({
                                                  ...socialQuestionOptions,
                                                  responseId: undefined,
                                                  isResolved: false,
                                              });
                                          },
                                      }
                                    : undefined
                            }
                            articleId={articleId}
                            additionalExtensions={[extMarkdown]}
                            onReport={(commentUri) => setReportProps({ articleId, commentUri, articleUri })}
                        />
                        <EditorsPanel />
                    </CommentsBlocWrapper>
                </>
            )}
            {isMarkdownActive && <MarkdownModal onInsert={onInsert} open={open} close={closeMarkdownModal} />}
            {reportProps && <AddReportModal onClose={() => setReportProps(null)} {...reportProps} />}
        </>
    );
};
